// InfoView: panel with topic/user info.
import React from 'react';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';

import { AccessMode, Tinode } from '../tinode/tinode';

import ErrorPanel from '../widgets/error-panel.jsx';
import GroupManager from '../widgets/group-manager.jsx';
import MenuCancel from '../widgets/menu-cancel.jsx';
import PermissionsEditor from '../widgets/permissions-editor.jsx';
import TopicCommonView from './topic-common-view.jsx';
import TopicSecurity from '../widgets/topic-security.jsx';

import { GROUP_MEMBER_VIEW_TYPE, NODE_TYPE_MESSAGE } from '../tinode/constants'

import { MAX_TITLE_LENGTH, MAX_TOPIC_DESCRIPTION_LENGTH, NO_ACCESS_MODE } from '../config.js';

import { makeImageUrl } from '../lib/blob-helpers.js';
import { theCard, clipStr } from '../lib/utils.js';
import InfoViewContent from '../widgets/info-view-content.jsx';
import MessagesUtil from '../tinode/messagesUtils';
import { profile } from '../stores/storages/ProfileStorage';
import Helpers from '../lib/helpers';
import { IQ_RESULT_CODE, GROUP_PRIVATE_STATE } from '../tinode/constants'
import StringUtils from '../lib/string';

const messages = defineMessages({
  info: {
    id: 'panel_title_info',
    description: 'Title for InfoView',
    defaultMessage: 'Info'
  },
  general: {
    id: 'panel_title_general',
    description: 'Title for TopicCommon.',
    defaultMessage: 'General'
  },
  security: {
    id: 'panel_title_security',
    description: 'Title for TopicSecirity and AccSecurity.',
    defaultMessage: 'Security'
  },
  members: {
    id: 'panel_title_members',
    description: 'Title for managing group members view.',
    defaultMessage: 'Members'
  },
  crop: {
    id: 'panel_title_crop',
    description: 'Title for AvatarCropView.',
    defaultMessage: 'Drag to Adjust'
  },
  perm_want: {
    id: 'requested_permissions',
    defaultMessage: 'Requested',
    description: 'Title for permissions'
  },
  perm_given: {
    id: 'granted_permissions',
    defaultMessage: 'Granted',
    description: 'Title for permissions'
  },
  perm_auth: {
    id: 'permissions_authenticated',
    defaultMessage: 'Authenticated',
    description: 'Title for editing default authenticated permissions'
  },
  perm_anon: {
    id: 'permissions_anonymous',
    defaultMessage: 'Anonymous',
    description: 'Title for editing default anonymous permissions'
  },
  perm_user: {
    id: 'permissions_user',
    defaultMessage: 'User\'s Permissions',
    description: 'Title for editing user\'s permissions'
  },
  edit_permissions: {
    id: 'menu_item_edit_permissions',
    defaultMessage: 'Edit permissions',
    description: 'Menu item [Edit permissions]'
  },
  button_leave_group: {
    id: 'button_leave_group',
    defaultMessage: 'Leave group',
    description: 'Flat button Leave group'
  },
  leave_group_warning: {
    id: 'leave_group_warning',
    defaultMessage: 'Are you sure you want to leave this group? It cannot be undone.',
    description: 'Alert dialog warning when leaving group'
  },
  leave_group_success: {
    id: 'leave_group_success',
    defaultMessage: 'Leave group success',
    description: 'Notify leave group success'
  },
  leave_group_failed: {
    id: 'leave_group_failed',
    defaultMessage: 'Leave group failed',
    description: 'Notify leave group failed'
  },
  set_group_private_success: {
		id: "set_group_private_success",
		defaultMessage: "You have changed the group privacy settings to private",
		description: "Notify private success",
  },
  set_group_public_success: {
		id: "set_group_public_success",
		defaultMessage: "{member} have changed the group privacy settings to public",
		description: "Notify public success",
  },
  badge_you: {
    id: "badge_you",
		defaultMessage: "You",
		description: "You",
  },
  set_group_private_failed: {
		id: "notify_error",
		defaultMessage: "An error occurred. Please try again later",
		description: "Notify error",
  },
  leave_group_notify: {
    id: 'leave_group_notify',
    defaultMessage: 'You have left this group',
    description: 'You have left this group'
  }
});

class InfoView extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      topic: null,
      owner: false,
      admin: false,
      sharer: false,
      deleter: false,
      muted: false,
      address: null,
      groupTopic: undefined,
      channel: undefined,
      fullName: undefined,
      description: undefined,
      avatar: null,
      private: null,
      selectedContact: null,
      access: null,
      modeGiven: null,
      modeWant: null,
      modeGiven2: null, // P2P topic, the other user mode given
      modeWant2: null,  // P2P topic, the other user mode want
      auth: null,
      anon: null,
      contactList: [],
      trustedBadges: [],
      previousMetaDesc: undefined,
      previousSubsUpdated: undefined,

      showGroupMembers: false,
    };

    this.resetSubs = this.resetSubs.bind(this);
    this.resetDesc = this.resetDesc.bind(this);
    this.resetTags = this.resetTags.bind(this);
    this.onMetaDesc = this.onMetaDesc.bind(this);
    this.onSubsUpdated = this.onSubsUpdated.bind(this);
    this.handleImageChanged = this.handleImageChanged.bind(this);
    this.handleMuted = this.handleMuted.bind(this);
    this.handleUnarchive = this.handleUnarchive.bind(this);
    this.handlePermissionsChanged = this.handlePermissionsChanged.bind(this);
    this.handleLaunchPermissionsEditor = this.handleLaunchPermissionsEditor.bind(this);
    this.handleShowAddMembers = this.handleShowAddMembers.bind(this);
    this.handleMemberUpdateRequest = this.handleMemberUpdateRequest.bind(this);
    this.handleMemberSelected = this.handleMemberSelected.bind(this);
    this.handleContextMenu = this.handleContextMenu.bind(this);
    this.handleBackNavigate = this.handleBackNavigate.bind(this);
    this.leaveGroup = this.leaveGroup.bind(this);
    this.dissolveGroup = this.dissolveGroup.bind(this);
    this.setGroupPrivate = this.setGroupPrivate.bind(this);
  }
  componentDidMount() {
    const {tinode, topic} = this.props;
    const topicInfo = tinode.getTopic(topic);
    this.resetSubs(topicInfo, this.props);
  }
  // No need to separately handle component mount.
  componentDidUpdate(props) {
    const topic = this.props.tinode.getTopic(props.topic);
    if (!topic) {
      return;
    }

    if (this.onMetaDesc != topic.onMetaDesc) {
      this.previousMetaDesc = topic.onMetaDesc;
      topic.onMetaDesc = this.onMetaDesc;

      this.previousSubsUpdated = topic.onSubsUpdated;
      topic.onSubsUpdated = this.onSubsUpdated;
    }

    if (this.state.topic != props.topic) {
      this.setState({ topic: props.topic });
      this.resetDesc(topic, props);
      this.resetSubs(topic, props);
      this.resetTags(topic);
    }
  }

  componentWillUnmount() {
    const topic = this.props.tinode.getTopic(this.props.topic);
    if (!topic) {
      return;
    }
    this.setState({ topic: null });
    topic.onMetaDesc = this.previousMetaDesc;
    topic.onSubsUpdated = this.previousSubsUpdated;
  }

  resetSubs(topic, props) {
    console.log('================resetSubs', topic, props)
    const newState = { contactList: [] };
    if (topic.getType() == 'p2p') {
      // Fetch the other party in the p2p conversation.
      // Topic may not be ready yet, so check if user is found.
      const user2 = topic.subscriber(props.topic);
      if (user2) {
        newState.modeGiven2 = user2.acs.getGiven();
        newState.modeWant2 = user2.acs.getWant();
      } else {
        newState.modeGiven2 = NO_ACCESS_MODE;
        newState.modeWant2 = NO_ACCESS_MODE;
      }
    } else {
      topic.subscribers((sub) => {
        try {
          if (sub && sub.acs) {
            sub.acs = new AccessMode(sub.acs)
          }
        } catch (e) {
          console.log(e)
          sub.acs = new AccessMode({
            given: 'JRWPASD',
            want: 'JRWPASD',
            mode: 'JRWPASD',
          })
        }
        newState.contactList.push(sub);
      }, this);
    }

    this.setState(newState);
  }

  resetDesc(topic, props) {
    const defacs = topic.getDefaultAccess() || {};
    const acs = topic.getAccessMode();

    const badges = [];
    if (topic.trusted) {
      for (const [key, val] of Object.entries(topic.trusted)) {
        if (val) {
          badges.push(key);
        }
      }
    }

    this.setState({
      owner: acs && acs.isOwner(),
      admin: acs && acs.isAdmin(),
      sharer: acs && acs.isSharer(),
      deleter: acs && acs.isDeleter(),
      muted: acs && acs.isMuted(),

      fullName: clipStr(topic.public && topic.public.fn, MAX_TITLE_LENGTH),
      description: clipStr(topic.public && topic.public.note, MAX_TOPIC_DESCRIPTION_LENGTH),
      avatar: makeImageUrl(topic.public ? topic.public.photo : null),
      trustedBadges: badges,
      private: clipStr(topic.private && topic.private.comment, MAX_TITLE_LENGTH),
      archived: topic.isArchived(),
      address: topic.name,
      groupTopic: topic.isGroupType(),
      channel: topic.isChannelType() || topic.chan,
      access: acs ? acs.getMode() : undefined,
      modeGiven: acs ? acs.getGiven() : undefined,
      modeWant: acs ? acs.getWant() : undefined,
      auth: defacs.auth,
      anon: defacs.anon
    });
  }

  resetTags(topic) {
    if (topic.getType() != 'grp') {
      return;
    }

    const acs = topic.getAccessMode();
    if (acs && acs.isOwner()) {
      // Requesting tags: owner is editing the topic.
      topic.getMeta(topic.startMetaQuery().withTags().build());
    }
  }

  onMetaDesc(desc) {
    const topic = this.props.tinode.getTopic(this.props.topic);
    if (!topic) {
      return;
    }
    this.resetDesc(topic, this.props);

    if (this.previousMetaDesc && this.previousMetaDesc != this.onMetaDesc) {
      this.previousMetaDesc(desc);
    }
  }

  onSubsUpdated(subs) {
    const topic = this.props.tinode.getTopic(this.props.topic);
    if (!topic) {
      return;
    }
    this.resetSubs(topic, this.props);

    if (this.previousSubsUpdated && this.previousSubsUpdated != this.onSubsUpdated) {
      this.previousSubsUpdated(subs);
    }
  }

  handleImageChanged(mime, img) {
    this.setState({ avatar: img });
    this.props.onTopicDescUpdate(this.props.topic, theCard(null, img || Tinode.DEL_CHAR), null);
  }

  handleMuted(ignored, checked) {
    this.setState({ muted: checked });
    this.props.onChangePermissions(this.props.topic, checked ? '-P' : '+P');
  }

  handleUnarchive(ignored, ignored2) {
    this.props.onTopicUnArchive(this.props.topic);
  }

  handlePermissionsChanged(which, perm) {
    switch (which) {
      case 'auth':
        this.props.onTopicDescUpdateRequest(this.props.topic, null, null, { auth: perm });
        break;
      case 'anon':
        this.props.onTopicDescUpdateRequest(this.props.topic, null, null, { anon: perm });
        break;
      case 'mode':
      case 'want':
        this.props.onChangePermissions(this.props.topic, perm);
        break;
      case 'given':
        this.props.onChangePermissions(this.props.topic, perm, this.props.topic);
        break;
      case 'user':
        this.props.onChangePermissions(this.props.topic, perm, this.state.userPermissionsEdited);
        break;
    }

    this.handleBackNavigate();
  }

  handleLaunchPermissionsEditor(which, uid) {
    const { formatMessage } = this.props.intl;
    let toEdit, toCompare, toSkip, titleEdit, titleCompare, userTitle, userAvatar;
    switch (which) {
      case 'mode':
        toEdit = this.state.access;
        break;
      case 'want':
        toEdit = this.state.modeWant;
        toCompare = this.state.modeGiven;
        if (this.state.owner) {
          // Don't allow owner to unset 'O' permission. All other permissions are OK.
          toSkip = 'O';
        } else {
          // Allow accepting any of 'ASDO' permissions but don't allow asking for them.
          toSkip = AccessMode.encode(AccessMode.diff('ASDO', this.state.modeGiven));
          if (this.state.channel) {
            // Channels are read-only.
            toSkip += 'W';
          }
        }
        titleEdit = formatMessage(messages.perm_want);
        titleCompare = formatMessage(messages.perm_given);
        break;
      case 'given':
        toEdit = this.state.modeGiven2;
        toCompare = this.state.modeWant2;
        toSkip = this.state.groupTopic ? (this.state.owner ? '' : 'O') : 'ASDO';
        titleEdit = formatMessage(messages.perm_given);
        titleCompare = formatMessage(messages.perm_want);
        break;
      case 'auth':
        toEdit = this.state.auth;
        toSkip = 'O';
        break;
      case 'anon':
        toEdit = this.state.anon;
        toSkip = 'O';
        break;
      case 'user': {
        const topic = this.props.tinode.getTopic(this.props.topic);
        if (!topic) {
          return;
        }
        const user = topic.subscriber(uid);
        if (!user || !user.acs) {
          return;
        }
        toEdit = user.acs.getGiven();
        toCompare = user.acs.getWant();
        toSkip = this.state.owner ? '' : 'O';
        titleEdit = formatMessage(messages.perm_given);
        titleCompare = formatMessage(messages.perm_want);
        if (user.public) {
          userTitle = user.public.fn;
          userAvatar = user.public.photo;
        }
        break;
      }
      default:
        console.error("Unknown permission editing mode '" + which + "'");
        return;
    }
    this.setState({
      userPermissionsEdited: uid,
      userPermissionsTitle: userTitle,
      userPermissionsAvatar: userAvatar,
      editedPermissions: toEdit,
      immutablePermissions: toCompare,
      editedPermissionsTitle: titleEdit,
      immutablePermissionsTitle: titleCompare,
      editedPermissionsSkipped: toSkip,
    });
    this.props.onNavigate(`perm/${which}`);
  }

  handleShowAddMembers(e) {
    e.preventDefault();
    this.props.handleShowGroupMembers({
      topicGroup: this.props.topic,
      groupView: GROUP_MEMBER_VIEW_TYPE.ADD_MEMBER,
      groupMembers: this.state.contactList,
    })
  }

  handleMemberUpdateRequest(members, added, removed) {
    this.props.onMemberUpdateRequest(this.props.topic, added, removed);
    this.props.onNavigate('info');
  }

  handleMemberSelected(uid) {
    this.setState({ selectedContact: uid });
  }

  leaveGroup() {
    const { formatMessage } = this.props.intl;
    this.props.tinode.sendRequestLeaveGroup(this.props.topic, MessagesUtil.xmppid()).then((resultData) => {
      switch (resultData.errorCode) {
        case IQ_RESULT_CODE.SUCCESS: {
          const contactList = this.state.contactList.filter(u => u.jid !== profile.username)
          this.setState({ contactList })
          Helpers.showSuccessToasts(formatMessage(messages.leave_group_success))

          const id = MessagesUtil.xmppid();
          const body = formatMessage(messages.leave_group_notify)
          this.props.tinode.addMessageNotify(this.props.topic, NODE_TYPE_MESSAGE.GROUPCHAT, {
            body,
            timesend: new Date().getTime(),
            id,
          })

          const topic = this.props.tinode.getTopicByName(this.props.topic)
          if (topic && topic.acs && topic.acs.isWriter()) {
            topic.updateMode(null, '-W')
          }
          this.handleBackNavigate();
          break
        }
        default: {
          Helpers.showErrorToasts(formatMessage(messages.leave_group_failed))
          break
        }
      }
      
    })
  }

  setGroupPrivate(groupPrivate) {
    return new Promise((resolve) => {
      const { formatMessage } = this.props.intl;
      const id = MessagesUtil.xmppid()
      const topicId = this.props.topic
      this.props.tinode.sendRequestGroupPrivate(topicId, groupPrivate, id).then((resultData) => {
        switch (resultData.errorCode) {
          case IQ_RESULT_CODE.SUCCESS: {
            const groupPrivate = resultData.groupPrivate
            if (groupPrivate === GROUP_PRIVATE_STATE.PRIVATE) {
              const body = formatMessage(messages.set_group_private_success, { member: StringUtils.capitalizeFirstLetter(formatMessage(messages.badge_you)) })
              Helpers.showSuccessToasts(body)
              this.props.tinode.addMessageNotify(topicId, NODE_TYPE_MESSAGE.GROUPCHAT, {
                body,
                timesend: new Date().getTime(),
                id,
              })
            } else if (groupPrivate === GROUP_PRIVATE_STATE.NON_PRIVATE) {
              const body = formatMessage(messages.set_group_public_success, { member: StringUtils.capitalizeFirstLetter(formatMessage(messages.badge_you)) })
              Helpers.showSuccessToasts(body)
              this.props.tinode.addMessageNotify(topicId, NODE_TYPE_MESSAGE.GROUPCHAT, {
                body,
                timesend: new Date().getTime(),
                id,
              })
            }
  
            break
          }
          default: {
            Helpers.showErrorToasts(formatMessage(messages.set_group_private_failed))
            break
          }
        }
  
        resolve(resultData)
        
      }).catch(e => {
        resolve(null)
      })
    })
    
  }

  dissolveGroup() {
    this.props.tinode.sendRequestDissolveGroup(this.props.topic, MessagesUtil.xmppid()).then((resultData) => {
      switch (resultData.errorCode) {
        case IQ_RESULT_CODE.SUCCESS: {
          window.logger("dissolveGroup|send Iq message SUCCESS.")
          break
        }
        default: {
          window.logger("dissolveGroup|send Iq message ERROR.")
          break
        }
      }
    })
  }

  handleBackNavigate() {
    const args = (this.props.panel || 'info').split('/');
    if (args[0] == 'info') {
      this.props.onNavigate(null);
    } else if (args[0] == 'perm') {
      if (args[1] == 'user') {
        this.props.onNavigate('info');
      } else {
        this.props.onNavigate('security');
      }
    } else {
      this.props.onNavigate('info');
    }
    this.props.handleSetInfoPanel()
  }

  handleContextMenu(params) {
    const { formatMessage } = this.props.intl;
    const topic = this.props.tinode.getTopic(this.props.topic);
    if (!topic) {
      return;
    }
    const user = topic.subscriber(params.topicName);
    if (!user || !user.acs) {
      return;
    }

    const isMe = this.props.tinode.isMe(params.topicName);
    const menuItems = [{
      title: formatMessage(messages.edit_permissions),
      handler: _ => this.handleLaunchPermissionsEditor(isMe ? 'want' : 'user', params.topicName)
    }];
    if (!isMe) {
      menuItems.push('member_delete');
    }
    menuItems.push(user.acs.isMuted() ? 'member_unmute' : 'member_mute');
    if (!isMe) {
      menuItems.push(user.acs.isJoiner() ? 'member_block' : 'member_unblock');
    }
    this.props.showContextMenu({
      topicName: this.props.topic,
      x: params.x,
      y: params.y,
      user: params.topicName
    }, menuItems);
  }

  render() {
    const args = (this.props.panel || 'info').split('/');
    const view = args[0];
    args.shift();

    const { formatMessage } = this.props.intl;
    const panelTitle = formatMessage((view == 'perm' ? messages['perm_' + args[0]] : messages[view])
      || messages['info']);

    return (
      <div id="info-view">
        <div className="caption-panel" id="info-caption-panel">
          <div>
            <MenuCancel onCancel={this.handleBackNavigate} />
          </div>
          <div className="panel-title" id="info-title">{panelTitle}</div>
        </div>
        {this.props.displayMobile ?
          <ErrorPanel
            level={this.props.errorLevel}
            text={this.props.errorText}
            onClearError={this.props.onError} /> : null}
        {view == 'members' ?
          <GroupManager
            tinode={this.props.tinode}
            members={this.state.contactList}
            requiredMember={this.props.myUserId}
            keepInitialMembers={!this.state.admin && !this.state.owner}
            myUserId={this.props.myUserId}
            contacts={this.props.searchableContacts}
            onCancel={this.handleBackNavigate}
            onSubmit={this.handleMemberUpdateRequest} />
          :
          view == 'perm' && args.length > 0 ?
            <PermissionsEditor
              tinode={this.props.tinode}
              mode={this.state.editedPermissions}
              compare={this.state.immutablePermissions}
              skip={this.state.editedPermissionsSkipped}
              modeTitle={this.state.editedPermissionsTitle}
              compareTitle={this.state.immutablePermissionsTitle}
              userTitle={this.state.userPermissionsTitle}
              item={this.state.userPermissionsEdited}
              userAvatar={this.state.userPermissionsAvatar}
              onSubmit={(mode) => this.handlePermissionsChanged(args[0], mode)}
              onCancel={this.handleBackNavigate} />
            :
            view == 'general' ?
              <TopicCommonView
                tinode={this.props.tinode}
                topic={this.props.topic}
                reqCredMethod={this.props.reqCredMethod}
                onCredAdd={this.props.onCredAdd}
                onUpdateTagsRequest={this.props.onTopicTagsUpdateRequest}
                onCredConfirm={this.props.onCredConfirm}
                onCredDelete={this.props.onCredDelete}
                onUpdateTopicDesc={this.props.onTopicDescUpdateRequest}
                onError={this.props.onError} />
              :
              view == 'security' ?
                <TopicSecurity
                  topic={this.props.topic}
                  owner={this.state.owner}
                  admin={this.state.admin}
                  sharer={this.state.sharer}
                  deleter={this.state.deleter}
                  muted={this.state.muted}

                  groupTopic={this.state.groupTopic}
                  channel={this.state.channel}
                  access={this.state.access}
                  modeGiven={this.state.modeGiven}
                  modeWant={this.state.modeWant}
                  modeGiven2={this.state.modeGiven2}
                  modeWant2={this.state.modeWant2}
                  auth={this.state.auth}
                  anon={this.state.anon}

                  onShowAlert={this.props.onShowAlert}
                  onDeleteMessages={this.props.onDeleteMessages}
                  onLeaveTopic={this.props.onLeaveTopic}
                  onBlockTopic={this.props.onBlockTopic}
                  onReportTopic={this.props.onReportTopic}
                  onLaunchPermissionsEditor={this.handleLaunchPermissionsEditor}
                  onNavigate={this.props.onNavigate} />
                :
                <InfoViewContent
                  topic={this.props.topic}
                  tinode={this.props.tinode}
                  admin={this.state.admin}       
                  handleShowGroupMembers={this.props.handleShowGroupMembers}
                  groupMembers={this.state.contactList}
                  onShowAlert={this.props.onShowAlert}
                  leaveGroup={this.leaveGroup}
                  setGroupPrivate={this.setGroupPrivate}
                  dissolveGroup={this.dissolveGroup}
                  onError={this.props.onError}
                  onCancel={this.handleBackNavigate}
                  onTopicSelected={this.props.onTopicSelected}
                  onShowDialog={this.props.onShowDialog}
                  avatarTopic={this.state.avatar}
                  handleSetInfoPanel={this.props.handleSetInfoPanel}
                />
        }
      </div>
    );
  }
};

export default injectIntl(InfoView);
